import { useContext, useEffect, useRef } from 'react';
import { StoreContext, namespace } from '@/context';
import {
  collectPagePerformancePayload,
  resolvePerformanceNamespace,
} from '@/utils/performance';

type Options = {
  enabled?: boolean;
  idleTimeout?: number;
};

export const usePerformanceReport = ({ enabled = true, idleTimeout = 2000 }: Options = {}) => {
  const { state } = useContext(StoreContext);
  const sensorsCtx = state?.[namespace.app]?.sensors;
  const hasReportedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (hasReportedRef.current) return;

    const track = sensorsCtx?.track;
    if (typeof track !== 'function') return;

    const win = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number;
        cancelIdleCallback?: (handle: number) => void;
      };

    const report = () => {
      if (hasReportedRef.current) return;

      const payload = collectPagePerformancePayload();
      if (!payload) return;

      const space = resolvePerformanceNamespace(window.location.pathname);
      track('page_load_performance', payload, space);
      hasReportedRef.current = true;
    };

    const schedule = () => {
      cleanupRef.current?.();

      if (typeof win.requestIdleCallback === 'function') {
        const id = win.requestIdleCallback(report, { timeout: idleTimeout });
        cleanupRef.current = () => win.cancelIdleCallback?.(id);
      } else {
        const timer = window.setTimeout(report, 0);
        cleanupRef.current = () => window.clearTimeout(timer);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      const onLoad = () => schedule();
      window.addEventListener('load', onLoad, { once: true });
      cleanupRef.current = () => window.removeEventListener('load', onLoad);
    }

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [enabled, idleTimeout, sensorsCtx?.track]);
};